<script setup>
import {computed, onMounted} from 'vue';
import {getProductViewSessionId} from '~/utils/productViewContext.js';
import Reviews from "~/components/organisms/Reviews.vue";
import Breadcrumbs from "~/components/organisms/Breadcrumbs.vue";
import Heading from "~/components/atoms/Heading.vue";
import ArticlesGrid from "~/components/organisms/ArticlesGrid.vue";
import Button from "~/components/atoms/Button.vue";
import {useUserStore} from "~/store/userStore";
import Shortcode from "~/components/molecules/Shortcode.vue";
import Plugin from "~/components/organisms/Plugin.vue";
import PluginsPage from "~/components/templates/PluginsPage.vue";
import ArticlesCarousel from "~/components/organisms/ArticlesCarousel.vue";
import ProductLabel from "~/components/molecules/ProductLabel.vue";
import GreenLabel from "~/components/atoms/GreenLabel.vue";
import Timer from "~/components/molecules/Timer.vue";

const route = useRoute()
const slug = route.params.slug.join('/');
const userStore = useUserStore();

if (route.query['admin-only'] === 'preview' && !userStore.isAdmin) {
  await navigateTo('/login?redirect=' + encodeURIComponent(route.fullPath))
}

const adminOnly = route.query['admin-only']
const data = await fetchWithErrorHandling(`/blog/${slug}${adminOnly ? `?admin-only=${adminOnly}` : ''}`)

if (
  route.query['admin-only'] !== 'preview' &&
  (data.value?.unpublished)
) {
  await navigateTo('/blogg/', { redirectCode: 302 })
}

const article = data.value?.article ?? {}
const breadcrumbs = data.value?.breadcrumbs ?? []
const related_articles = data.value?.related_articles ?? []
const side_articles = data.value?.side_articles ?? []

const filterNiche = computed(() => {
  return article?.filter_niche ?? null;
});

const timerDate = computed(() => {
  const rawDate = filterNiche.value?.filter_niche_campaign?.date;
  if (!rawDate) return '';
  return rawDate.toString().replace('T', ' ').split('.')[0];
});

const themeData = computed(() => {
  return article?.theme ?? null;
});

const themeLabel = computed(() => {
  const theme = themeData.value;
  if (!theme) return "";

  const name = typeof theme.name === "string" ? theme.name.trim() : "";
  if (name) return name;
  
});

const themeStyle = computed(() => {
  const theme = themeData.value;
  if (!theme) return {};

  return {
    background: theme.background_color || undefined,
    color: theme.color || undefined,
  };
});

const grouped_plugins = computed(() =>
    data.value?.grouped_plugins ?? data.value?.data?.grouped_plugins ?? {}
);


onMounted(() => {
  const segments = slug.split('/').filter(Boolean)
  const articleSlug = segments[segments.length - 1]

  if (!articleSlug) {
    return
  }

  useCustomFetch('page-view', {
    method: 'POST',
    body: {
      viewable_type: 'article',
      slug: articleSlug,
      session_id: getProductViewSessionId(),
      source_url: window.location.href,
      referrer: document.referrer || null,
    },
  }, true).catch(() => {
  })
})

useHead({
  title: article?.meta_title ?? '',
  meta: [
    {
      hid: 'description',
      name: 'description',
      content: article?.meta_description ?? ''
    },
    {
      hid: 'og:title',
      property: 'og:title',
      content: article?.meta_facebook_title ?? ''
    },
    {
      hid: 'og:description',
      property: 'og:description',
      content: article?.meta_facebook_description ?? ''
    },
    {
      hid: 'og:image',
      property: 'og:image',
      content: article?.facebook_image ?? ''
    }
  ],
  script: [
    {
      type: 'application/ld+json',
      children: data.value?.breadcrumbs_structured_data ?? []
    },
    {
      type: 'application/ld+json',
      children: data.value?.structured_data ?? []
    },
  ]
})

</script>

<template>
  <div class="gap">
    <div v-if="route.query['admin-only'] === 'preview'" class="preview-banner">
      Preview mode
    </div>
    <div class="container full-width">
      <div class="breadcrumbs-row">
        <Breadcrumbs :breadcrumbs="breadcrumbs"/>
        <div class="labels">
          <ProductLabel 
            v-if="themeLabel" 
            :name="themeLabel" 
            :background="themeStyle.background"
            :color="themeStyle.color"
          />
          <GreenLabel
            v-if="filterNiche && filterNiche.name"
            :background="filterNiche.background_color"
            :color="filterNiche.color"
          >
            {{ filterNiche.name }}
          </GreenLabel>
        </div>
      </div>

      <div class="g-8 full-width items-center justify-center">
        <Heading style="margin: auto;" tag="h1" align="center" font-size="36">{{ article?.name ?? '' }}</Heading>
        <Heading style="margin: auto;" tag="h2" align="center" font-weight="400" font-size="16px">{{ article?.subheading ?? '' }}</Heading>
      </div>
      <Timer 
        class="campaign-timer"
        v-if="filterNiche?.filter_niche_campaign"
        :date="timerDate || ''"
        :text="filterNiche.filter_niche_campaign.promo_name || ''"
        :background="filterNiche.filter_niche_campaign.background || '#000'" 
        :color="filterNiche.filter_niche_campaign.color || '#fff'"
        :timer_color="filterNiche.filter_niche_campaign.color || '#fff'"
        style="margin-bottom: 24px;"
      />
    </div>

    <div class="container">
      <div class="full-width">
        <div class="main-container">
          <div class="main-image old" v-if="!article.published">
            <NuxtImg :src="article?.image ?? ''"/>
          </div>
          <!--          <div class="author row">-->
          <!--            <div class="avatar">-->
          <!--              <NuxtImg :src="article?.user?.avatar ?? ''"/>-->
          <!--            </div>-->
          <!--            <div class="info row">-->
          <!--              {{ $t('blog.author') }} {{ article?.user?.name ?? '' }} {{ article?.date ?? '' }}-->
          <!--            </div>-->
          <!--          </div>-->

          <div class="custom-html">
            <div class="blog-text " v-if="!article?.published && article?.text" v-html="article?.text ?? ''"></div>
            <PluginsPage v-else-if="article?.published && Object.keys(grouped_plugins ?? {}).length">
              <template v-for="[position, plugins] of Object.entries(grouped_plugins)" v-slot:[position]>
                <template v-for="plugin in plugins" :key="plugin.id ?? plugin.name">
                  <Plugin
                      v-if="plugin.data !== false"
                      :data="plugin.data"
                      :name="plugin.name"
                      :settings="plugin.settings"
                  />
                </template>
              </template>
            </PluginsPage>
          </div>
        </div>
      </div>
    </div>

    <div class="blog-carousel-wrapper">
      <ArticlesCarousel
        v-if="side_articles.length"
        :articles="side_articles"
        :title="$t('blog.related_articles')"
        :showSeeAll="true"
      />
    </div>

    <div class="reviews-wrapper" v-if="article?.reviews">
      <div class="reviews-container">
        <Reviews :data="article?.reviews"/>
      </div>
    </div>
    
    <Shortcode
      v-if="userStore.isAdmin"
      :shortcodes="{
        url: article?.shortcodes?.[0]?.shortcode_url ?? '',
        name: article?.shortcodes?.[0]?.shortcode_name ?? ''
      }"
    />
  </div>

</template>

<style scoped lang="scss">
.reviews-container{
  margin: auto;
  max-width: 1545px;
  padding: 0 40px;
  width: 100%;
  background-color: #F9F9F9;
  margin-bottom: 40px;
  margin-top: 40px;
  @include smd {
    padding: 0 10px;
  }
}
.reviews-wrapper{
  width: 100%;
  margin-top: -60px;
  background-color: #F9F9F9;
}
.reviews-plugin-wrapper {
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  background-color: #f9f9f9;
  padding: 60px 0;
  margin-top: -60px;
  overflow: hidden;

  @include smd {
    padding: 40px 0;
    margin-top: -16px;
  }
}

.reviews-plugin-container {
  max-width: 1545px;
  margin: 0 auto;
  padding: 0 20px;

  @include smd {
    padding: 0 16px;
  }
}

.gap {
  gap: 60px;

  @include smd {
    gap: 16px;
  }
}
.blog-carousel-wrapper {
  padding-top: 70px;
  background-color: #f9f9f9;
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
}

.blog-carousel-wrapper :deep(.articles-carousel-section) {
  margin-bottom: 0;
}

.container{
  max-width: 1120px;
}

.breadcrumbs-row {
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  gap: 12px;
  width: 100%;

  @include smd {
    flex-direction: column;
    align-items: flex-start;
  }
}



.labels {
  display: flex !important;
  gap: 12px;
  flex-direction: row;
  align-items: center;
  margin-left: auto;
  flex: 0 0 auto;
  width: fit-content;

  @include smd {
    margin-left: 0;
    width: 100%;
    justify-content: flex-start;
    margin-top: 8px;
  }
}

.blog-text {
  text-align: justify;

  &:deep(h2) {
    margin: 0;
  }

  &:deep(.custom-html) {
    margin-top: 0 !important;
  }

  &:deep(.blog-text) {
    margin-top: 0 !important;

    a {
      color: $blue !important;
    }
  }

  &:deep(img) {
    margin: 20px 0 !important;
  }
}

.campaign-timer {
  @include smd {
    margin-left: -16px;
    margin-right: -16px;
    width: auto;

    :deep(.counter) {
      border-radius: 0 !important;
      border-left: none !important;
      border-right: none !important;
      padding: 0 20px;
    }
  }
}

.author {
  gap: 20px;
  color: $dark-black;

  .avatar {
    width: 50px;
    height: 50px;
    overflow: hidden;

    img {
      width: 100%;
      border-radius: 50%;
    }
  }
}

.container, .main-container {
  gap: 30px;
}



.main-container {
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding-right: 0 !important;

  @include smd {
    padding: 0;
  }
}

.main-image {
  width: 100%;

  img {
    width: 100%;
  }

  &.old {
    aspect-ratio: 1 / 0.4;
    overflow: hidden;
  }
}

.custom-html:deep(.blog-text) {
  margin-top: 30px;

  .category-h2 {
    margin-left: 0;
    font-size: 17px;
    font-weight: 600;
    margin-top: 0;
    margin-bottom: 15px;
    clear: both;
  }

  h3 {
    text-transform: uppercase;
    font-family: 'Raleway', sans-serif;
    font-size: 29px;
    font-weight: 600;
  }

  img {
    width: 100%;
  }

  .row {
    padding: 0;
    position: relative;
    gap: 20px;
    width: 100%;
    align-items: flex-start;

    @include smd {
      flex-direction: column;
      gap: 0;
    }

    &.mobile-50 {

      & > .cell {
        @include smd {
          width: 50% !important;
          display: inline-block;
        }
      }
    }

    &.mobile-100 {

      & > .cell {
        @include smd {
          width: 100% !important;
          display: inline-block;
        }
      }
    }
  }

  .cell {
    height: auto;
    position: relative;
    padding: 10px;
    overflow: visible;

    @include smd {
      width: 100%;
    }
  }

  ul {
    list-style-type: unset;
    padding: 0;
    margin-left: 20px;
  }

  .no-padding {
    padding: 0 !important;

    & > .cell {
      padding: 0 !important;
    }
  }

  .padding-bottom-5-mobile {
    @include smd {
      padding-bottom: 5px !important;
    }
  }

  .round {
    border-radius: 5px;
  }

  .justify {
    text-align: justify;
  }

  a {
    padding: 0 !important;
    overflow: visible;
  }

  & > .row {
    padding: 0;
  }

  .calculator-img {
    transform: translateY(20px);
  }

  .home-h1 {
    font-size: 15px;
    color: $grey;
    margin-bottom: 3px;
  }

  .padding-5 {
    padding: 5px !important;
  }

  .blog-text {
    margin-bottom: 24px !important;
    padding: 0 !important;
    color: $black !important;
    text-align: justify !important;
    font-size: 14px !important;

    span {
      color: $black !important;
    }
  }

  .blog-img {
    position: relative;
    display: inline-block;
    max-width: 100%;

    @include medium {
      width: 100%;
      margin-left: 0px;
      padding-right: 5px;
    }

    .text {
      position: absolute;
      bottom: 30px;
      background: #000;
      color: #fff;
      padding: 5px 10px !important;
      border-radius: 0 5px 5px 0;
      left: -5px;
    }

    img {

      @include medium {
        max-width: 100% !important;
      }
    }

  }

  .intro {
    display: block;
    width: 100%;

    .navigator {
      float: right;
      margin-left: 20px;

      @include smd {
        float: none;
        margin-bottom: 20px;
        margin-left: 0;
      }
    }

    .blog-text {
      display: block;
    }
  }

  .intro-text {
    padding: 33px 10px 0 0;
    vertical-align: top;

    @include smd {
      padding: 0;
    }
  }

  .blog {
    padding: 0;
    width: auto;

    h2 {
      font-size: 16px;
      margin-bottom: 20px;
      margin-top: 42px;
      font-weight: 600;
      color: $black;
    }
  }

  .product-category {
    color: #707070;

    & > .cell {
      width: 50%;

      @include tablets {
        grid-gap: 10px;
        column-gap: 10px;
        display: grid;
        grid-template-columns: repeat(1, 1fr);
      }
      @include small-tablets {
        grid-gap: 10px;
        column-gap: 10px;
        display: grid;
        grid-template-columns: repeat(1, 1fr);
      }

      @include smd {
        width: 100%;
      }

      &:first-child {
        padding-left: 60px;
        padding-right: 15px;
        @include medium {
          padding-left: 2px;
          padding-right: 15px;
        }
        @include tablets {
          width: 100%;
        }

        @include smd {
          padding: 0;
        }
      }

      &:last-child {
        padding-left: 15px;
        padding-right: 60px;

        @include medium {
          padding-left: 2px;
          padding-right: 15px;
        }
        @include tablets {
          padding-left: 20px;
          width: 100%;
        }
        @include smd {
          padding: 0;
        }
      }
    }

    h2 {
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 20px;
      margin-top: 40px;
      color: black;

      &:first-child {
        margin-top: 0;
      }
    }

    .blog-text {
      font-size: 14px !important;
    }

    .text {
      font-size: 14px;
    }
  }

  .navigator {
    background: #f5f5f5;
    padding: 25px;
    font-size: 14px;
    border-radius: 5px;
    color: black;

    .title {
      font-weight: 600;
    }

    ol {
      margin: 10px 0;
      padding-left: 15px;

      li {
        line-height: 29px;

        &::marker {
          color: #6e6e6e;
        }

        a {
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }
}

.preview-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: #fcc000;
  color: #000;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  padding: 6px;
  letter-spacing: 1px;
  text-transform: uppercase;
}
</style>
